import type { Segment } from '@/lib/types'
import type { EvidenceRow } from '@/lib/ensemble/types'

// Deterministic seed — always same demo data
export const DEMO_PROJECT_ID = 'proj_saas_churn_demo'
export const DEMO_PANEL_ID = 'panel_saas_churn_demo'
export const DEMO_TENANT_ID = 'tnt_demo'
export const DEMO_WORKSPACE_ID = 'wks_demo'

export const DEMO_PRE_READ_QUESTION = 'Are stated churn reasons accurate, or are they masking activation failures?'

export const DEMO_STIMULI: Array<{ stimulus_id: string; label: string; content: string; type: string }> = [
  {
    stimulus_id: 'stim_a',
    label: 'Option A',
    content: 'Too expensive — the price was higher than the value I received.',
    type: 'survey_option',
  },
  {
    stimulus_id: 'stim_b',
    label: 'Option B',
    content: 'Not enough features — the product was missing capabilities I needed.',
    type: 'survey_option',
  },
  {
    stimulus_id: 'stim_c',
    label: 'Option C',
    content: 'Too hard to use — I couldn\'t figure out how to make it work for me.',
    type: 'survey_option',
  },
  {
    stimulus_id: 'stim_d',
    label: 'Option D',
    content: 'Didn\'t use it enough — I ran out of time or the project changed.',
    type: 'survey_option',
  },
]

export const DEMO_SEGMENTS: Segment[] = [
  {
    segment_id: 'seg_low_arr',
    tenant_id: DEMO_TENANT_ID,
    workspace_id: DEMO_WORKSPACE_ID,
    project_id: DEMO_PROJECT_ID,
    upload_id: 'upload_saas_churn_demo',
    name: 'Low-ARR Churners $0–50/mo',
    respondent_count: 489,
    demographic_skew: { avg_arr_monthly: '$28', avg_sessions_before_cancel: 1.8, avg_days_to_cancel: 12 },
    dominant_themes: [
      'Price cited but usage data reveals activation failure',
      'Product never fully set up before cancellation',
      'No in-app guidance reached this cohort',
    ],
    evidence_strength: 'High',
    representative_quotes: [
      { text: 'I signed up and then just never got around to setting it up properly. I said price but honestly I barely tried it.', segment: 'Low-ARR Churners $0–50/mo', source_file: 'churn-exit-survey.csv', source_row: 44 },
      { text: 'I logged in twice. It was confusing and I didn\'t know where to start. I canceled because it felt too expensive for something I wasn\'t using.', segment: 'Low-ARR Churners $0–50/mo', source_file: 'churn-exit-survey.csv', source_row: 112 },
      { text: 'Price was my answer but if I\'m honest I never really activated. I just kept paying for something I wasn\'t touching.', segment: 'Low-ARR Churners $0–50/mo', source_file: 'churn-interviews.csv', source_row: 7 },
    ],
    created_at: 1700000000000,
  },
  {
    segment_id: 'seg_mid_arr',
    tenant_id: DEMO_TENANT_ID,
    workspace_id: DEMO_WORKSPACE_ID,
    project_id: DEMO_PROJECT_ID,
    upload_id: 'upload_saas_churn_demo',
    name: 'Mid-ARR Churners $51–200/mo',
    respondent_count: 203,
    demographic_skew: { avg_arr_monthly: '$112', avg_sessions_before_cancel: 7.2, avg_days_to_cancel: 47 },
    dominant_themes: [
      'Genuine feature gaps blocking core workflows',
      'Users activated but hit walls in advanced use cases',
      'Competitive tools had specific missing capabilities',
    ],
    evidence_strength: 'High',
    representative_quotes: [
      { text: 'I used it regularly but we needed bulk export and it wasn\'t there. That\'s a real gap, not an excuse.', segment: 'Mid-ARR Churners $51–200/mo', source_file: 'churn-exit-survey.csv', source_row: 634 },
      { text: 'I got deep into the product. The feature I needed was on the roadmap for 18 months and never shipped. That\'s why I left.', segment: 'Mid-ARR Churners $51–200/mo', source_file: 'churn-interviews.csv', source_row: 31 },
      { text: 'We were active users. We outgrew what the product could do. It wasn\'t the price — it was capability.', segment: 'Mid-ARR Churners $51–200/mo', source_file: 'churn-exit-survey.csv', source_row: 701 },
    ],
    created_at: 1700000000000,
  },
  {
    segment_id: 'seg_high_arr',
    tenant_id: DEMO_TENANT_ID,
    workspace_id: DEMO_WORKSPACE_ID,
    project_id: DEMO_PROJECT_ID,
    upload_id: 'upload_saas_churn_demo',
    name: 'High-ARR Churners $201+/mo',
    respondent_count: 87,
    demographic_skew: { avg_arr_monthly: '$380', avg_sessions_before_cancel: 23.4, avg_days_to_cancel: 134 },
    dominant_themes: [
      'Competitive displacement by enterprise-tier alternatives',
      'Deep power users who evaluated and switched deliberately',
      'Security, compliance, or SLA requirements drove the move',
    ],
    evidence_strength: 'Medium',
    representative_quotes: [
      { text: 'We were heavy users. Our security team required SOC 2 Type II and a competitor had it. That\'s why we moved.', segment: 'High-ARR Churners $201+/mo', source_file: 'churn-interviews.csv', source_row: 56 },
      { text: 'We loved the product. Our procurement team required an enterprise contract with SLA guarantees. You didn\'t offer that tier.', segment: 'High-ARR Churners $201+/mo', source_file: 'churn-exit-survey.csv', source_row: 889 },
      { text: 'The switch was a deliberate business decision. We evaluated three platforms over four months. Not about price, not about features — about vendor maturity.', segment: 'High-ARR Churners $201+/mo', source_file: 'churn-interviews.csv', source_row: 63 },
    ],
    created_at: 1700000000000,
  },
]

export const DEMO_EVIDENCE: EvidenceRow[] = [
  { respondent_id: 'R001', segment: 'Low-ARR Churners $0–50/mo', response: 'Option A', open_text: 'I signed up and then just never got around to setting it up properly. I said price but honestly I barely tried it.', source_file: 'churn-exit-survey.csv', row_index: 44 },
  { respondent_id: 'R002', segment: 'Low-ARR Churners $0–50/mo', response: 'Option A', open_text: 'I logged in twice. It was confusing and I didn\'t know where to start. I canceled because it felt too expensive for something I wasn\'t using.', source_file: 'churn-exit-survey.csv', row_index: 112 },
  { respondent_id: 'R003', segment: 'Low-ARR Churners $0–50/mo', response: 'Option A', open_text: 'Price was my answer but if I\'m honest I never really activated. I just kept paying for something I wasn\'t touching.', source_file: 'churn-interviews.csv', row_index: 7 },
  { respondent_id: 'R004', segment: 'Low-ARR Churners $0–50/mo', response: 'Option D', open_text: 'Never finished onboarding. There was no walkthrough that told me what to do first. I drifted away within two weeks.', source_file: 'churn-exit-survey.csv', row_index: 189 },
  { respondent_id: 'R005', segment: 'Mid-ARR Churners $51–200/mo', response: 'Option B', open_text: 'I used it regularly but we needed bulk export and it wasn\'t there. That\'s a real gap, not an excuse.', source_file: 'churn-exit-survey.csv', row_index: 634 },
  { respondent_id: 'R006', segment: 'Mid-ARR Churners $51–200/mo', response: 'Option B', open_text: 'I got deep into the product. The feature I needed was on the roadmap for 18 months and never shipped. That\'s why I left.', source_file: 'churn-interviews.csv', row_index: 31 },
  { respondent_id: 'R007', segment: 'Mid-ARR Churners $51–200/mo', response: 'Option B', open_text: 'We were active users. We outgrew what the product could do. It wasn\'t the price — it was capability.', source_file: 'churn-exit-survey.csv', row_index: 701 },
  { respondent_id: 'R008', segment: 'Mid-ARR Churners $51–200/mo', response: 'Option B', open_text: 'Our workflow required an API endpoint that didn\'t exist. We switched to a competitor that had it. Simple as that.', source_file: 'churn-exit-survey.csv', row_index: 758 },
  { respondent_id: 'R009', segment: 'High-ARR Churners $201+/mo', response: 'Option B', open_text: 'We were heavy users. Our security team required SOC 2 Type II and a competitor had it. That\'s why we moved.', source_file: 'churn-interviews.csv', row_index: 56 },
  { respondent_id: 'R010', segment: 'High-ARR Churners $201+/mo', response: 'Option B', open_text: 'We loved the product. Our procurement team required an enterprise contract with SLA guarantees. You didn\'t offer that tier.', source_file: 'churn-exit-survey.csv', row_index: 889 },
  { respondent_id: 'R011', segment: 'High-ARR Churners $201+/mo', response: 'Option B', open_text: 'The switch was a deliberate business decision. We evaluated three platforms over four months. Not about price, not about features — about vendor maturity.', source_file: 'churn-interviews.csv', row_index: 63 },
  { respondent_id: 'R012', segment: 'Low-ARR Churners $0–50/mo', response: 'Option A', open_text: 'I selected "too expensive" but I only used it once. I think the real reason is I didn\'t understand how to get value from it quickly.', source_file: 'churn-exit-survey.csv', row_index: 247 },
]
